import { Injectable, EventEmitter, OnDestroy } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import {
  OCCTWorkerRequest,
  OCCTWorkerResponse,
  OCCTWorkerRequestType,
  TessellationResult,
  ExtractEdgesResult,
  ExtractFacesResult,
  BooleanOperationResult,
  FilletChamferResult,
  ShellOffsetResult,
  ExtrudeResult,
  PushPullResult,
  StepImportResult,
  ExportStepResult,
  HealShapeResult,
  WorkerTessellationOptions,
  InitResult,
  BooleanPayload,
  FilletAllPayload,
  FilletEdgesPayload,
  ChamferAllPayload,
  ChamferEdgesPayload,
  TessellatePayload,
  ExtractEdgesPayload,
  ExtractFacesPayload,
  ShellPayload,
  OffsetPayload,
  PushPullPayload,
  ExtrudePayload,
  ImportStepPayload,
  ExportStepPayload,
  HealShapePayload,
  StepImportOptions,
  StreamingShapeData,
  StreamingImportComplete,
  // Worker-only architecture types
  CreateBoxPayload,
  CreateCylinderPayload,
  CreateSpherePayload,
  CreateRingPayload,
  CreateConePayload,
  CreateSlotPayload,
  CreateShapeResult,
  CloneShapePayload,
  DeleteShapePayload,
  DeleteShapesPayload,
  DeleteShapeResult,
  DeleteShapesResult,
  TransformShapePayload,
  TransformShapeResult,
  GetShapeInfoPayload,
  ShapeInfo,
  // V2 types (shapeId-based operations)
  BooleanPayloadV2,
  BooleanOperationResultV2,
  FilletAllPayloadV2,
  FilletEdgesPayloadV2,
  ChamferAllPayloadV2,
  ChamferEdgesPayloadV2,
  FilletChamferResultV2,
  ShellPayloadV2,
  OffsetPayloadV2,
  PushPullPayloadV2,
  ShellOffsetResultV2,
  PushPullResultV2,
  ExtrudePayloadV2,
  ExtrudeResultV2,
  TessellatePayloadV2,
  ExtractEdgesPayloadV2,
  ExtractFacesPayloadV2,
  ExtractFaceWiresPayloadV2,
  ExtractFaceWiresResult,
  // Import/Export V2
  ImportStepPayloadV2,
  StepImportResultV2,
  ExportStepPayloadV2,
  ExportStepResultV2,
  ExportStepMultiPayloadV2,
  ExportStepMultiResultV2,
  // Heal V2
  HealShapePayloadV2,
  HealShapeResultV2,
  // Extract Face / Thicken Surface V2
  ExtractFacePayloadV2,
  ExtractFaceResultV2,
  ExtractMultipleFacesPayloadV2,
  ThickenSurfacePayloadV2,
  ThickenSurfaceResultV2,
  // BREP Serialization
  ExportBrepPayload,
  ExportBrepResult,
  ImportBrepPayload,
  ImportBrepResult,
  // Preserve Geometry
  PreserveImportAssemblyPayload,
  PreserveImportAssemblyResult,
  PreservePreviewPayload,
  PreservePreviewResult,
  PreserveGeneratePayload,
  PreserveGenerateResult,
  PreserveGenerateFromClicksPayload,
  PreserveGenerateFromClicksResult,
  PreserveConnectivityPayload,
  PreserveConnectivityResult
} from './cad-kernel-worker.types';

/** Default timeout for worker tasks (2 minutes) */
const WORKER_TASK_TIMEOUT_MS = 120_000;

interface PendingTask {
  resolve: (value: any) => void;
  reject: (error: any) => void;
  startTime: number;
  timeoutId: ReturnType<typeof setTimeout>;
}

/**
 * Service for communicating with the OCCT Web Worker.
 *
 * This service manages the worker lifecycle and provides async methods
 * for all CPU-intensive CAD operations. Operations are offloaded to
 * a separate thread to keep the UI responsive.
 *
 * Usage:
 * 1. Inject the service
 * 2. Call init() to load OCCT WASM in the worker
 * 3. Use async methods for CAD operations
 */
@Injectable({
  providedIn: 'root'
})
export class CADKernelWorkerService implements OnDestroy {
  private worker: Worker | null = null;
  private pendingTasks = new Map<string, PendingTask>();
  private taskIdCounter = 0;
  private initPromise: Promise<void> | null = null;

  // State
  private _isReady = false;
  private _isSupported = typeof Worker !== 'undefined';

  // Observables
  public isReady$ = new BehaviorSubject<boolean>(false);
  public isProcessing$ = new BehaviorSubject<boolean>(false);

  // Events
  public onReady = new EventEmitter<void>();
  public onError = new EventEmitter<Error>();
  public onProgress = new EventEmitter<{ taskId: string; progress: number }>();
  public onStreamShape = new EventEmitter<StreamingShapeData>();
  public onStreamComplete = new EventEmitter<StreamingImportComplete>();

  constructor() {
    if (this._isSupported) {
      this.initWorker();
    } else {
      console.warn('CADKernelWorkerService: Web Workers not supported');
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // WORKER LIFECYCLE
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Initialize the worker (creates the worker instance)
   */
  private initWorker(): void {
    try {
      this.worker = new Worker(
        new URL('./cad-kernel.worker', import.meta.url),
        { type: 'module' }
      );

      this.worker.onmessage = (event: MessageEvent<OCCTWorkerResponse>) => {
        this.handleMessage(event.data);
      };

      this.worker.onerror = (error) => {
        console.error('CADKernelWorker error:', error);
        this.onError.emit(new Error(error.message));
      };

      console.log('CADKernelWorkerService: Worker instance created');
    } catch (e) {
      console.error('CADKernelWorkerService: Failed to create worker', e);
      this._isSupported = false;
    }
  }

  /**
   * Initialize OCCT WASM in the worker
   * Must be called before using any CAD operations
   */
  async init(wasmPath: string = '/assets/opencascade'): Promise<void> {
    if (this._isReady) {
      return;
    }

    if (this.initPromise) {
      return this.initPromise;
    }

    if (!this._isSupported || !this.worker) {
      throw new Error('Web Worker not supported or not initialized');
    }

    this.initPromise = this.submitTask<InitResult>('init', { wasmPath })
      .then((result) => {
        if (result.ready) {
          this._isReady = true;
          this.isReady$.next(true);
          this.onReady.emit();
          console.log('CADKernelWorkerService: OCCT initialized in worker');
        }
      })
      .catch((error) => {
        this.initPromise = null;
        throw error;
      });

    return this.initPromise;
  }

  /**
   * Check if the worker is ready
   */
  get isReady(): boolean {
    return this._isReady;
  }

  /**
   * Check if workers are supported
   */
  get isSupported(): boolean {
    return this._isSupported;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // MESSAGE HANDLING
  // ═══════════════════════════════════════════════════════════════════════════

  private handleMessage(response: OCCTWorkerResponse): void {
    const pending = this.pendingTasks.get(response.id);
    if (!pending) {
      console.warn('CADKernelWorkerService: Received response for unknown task', response.id);
      return;
    }

    if (response.type === 'progress') {
      this.onProgress.emit({ taskId: response.id, progress: response.progress || 0 });
      return; // Don't resolve yet, wait for success/error
    }

    // Handle streaming shape - emit the shape but keep task pending
    if (response.type === 'stream-shape') {
      this.onStreamShape.emit(response.payload as StreamingShapeData);
      return;
    }

    // Handle streaming complete - emit completion then resolve
    if (response.type === 'stream-complete') {
      this.onStreamComplete.emit(response.payload as StreamingImportComplete);
      // Continue to resolve the task below
    }

    this.pendingTasks.delete(response.id);
    clearTimeout(pending.timeoutId);

    // Update processing state
    if (this.pendingTasks.size === 0) {
      this.isProcessing$.next(false);
    }

    const duration = Date.now() - pending.startTime;

    if (response.type === 'success' || response.type === 'stream-complete') {
      console.log(`CADKernelWorkerService: Task ${response.id} completed in ${duration}ms`);
      pending.resolve(response.payload);
    } else {
      console.error(`CADKernelWorkerService: Task ${response.id} failed:`, response.error);
      pending.reject(new Error(response.error || 'Unknown worker error'));
    }
  }

  /**
   * Generate unique task ID
   */
  private generateTaskId(): string {
    return `occt_${++this.taskIdCounter}_${Date.now()}`;
  }

  /**
   * Submit a task to the worker
   */
  private submitTask<T>(type: OCCTWorkerRequestType, payload: any): Promise<T> {
    return new Promise((resolve, reject) => {
      if (!this.worker) {
        reject(new Error('Worker not initialized'));
        return;
      }

      // For non-init tasks, ensure OCCT is initialized
      if (type !== 'init' && !this._isReady) {
        reject(new Error('OCCT not initialized. Call init() first.'));
        return;
      }

      const id = this.generateTaskId();
      const request: OCCTWorkerRequest = { id, type, payload };

      const timeoutId = setTimeout(() => {
        const task = this.pendingTasks.get(id);
        if (task) {
          this.pendingTasks.delete(id);
          if (this.pendingTasks.size === 0) {
            this.isProcessing$.next(false);
          }
          task.reject(new Error(`Worker task ${type} (${id}) timed out after ${WORKER_TASK_TIMEOUT_MS}ms`));
        }
      }, WORKER_TASK_TIMEOUT_MS);

      this.pendingTasks.set(id, {
        resolve,
        reject,
        startTime: Date.now(),
        timeoutId
      });

      this.isProcessing$.next(true);

      try {
        this.worker.postMessage(request);
      } catch (e) {
        this.pendingTasks.delete(id);
        if (this.pendingTasks.size === 0) {
          this.isProcessing$.next(false);
        }
        reject(e instanceof Error ? e : new Error(`Failed to post message to worker: ${e}`));
      }
    });
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // BOOLEAN OPERATIONS
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Boolean union of two shapes
   */
  async booleanUnion(
    brepA: string,
    brepB: string,
    tessellationOptions?: WorkerTessellationOptions
  ): Promise<BooleanOperationResult> {
    const payload: BooleanPayload = { brepA, brepB, tessellationOptions };
    return this.submitTask('boolean-union', payload);
  }

  /**
   * Boolean subtraction (cut shape B from shape A)
   */
  async booleanSubtract(
    brepA: string,
    brepB: string,
    tessellationOptions?: WorkerTessellationOptions
  ): Promise<BooleanOperationResult> {
    const payload: BooleanPayload = { brepA, brepB, tessellationOptions };
    return this.submitTask('boolean-subtract', payload);
  }

  /**
   * Boolean intersection of two shapes
   */
  async booleanIntersect(
    brepA: string,
    brepB: string,
    tessellationOptions?: WorkerTessellationOptions
  ): Promise<BooleanOperationResult> {
    const payload: BooleanPayload = { brepA, brepB, tessellationOptions };
    return this.submitTask('boolean-intersect', payload);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // FILLET OPERATIONS
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Apply fillet to all edges
   */
  async filletAllEdges(
    brep: string,
    radius: number,
    tessellationOptions?: WorkerTessellationOptions
  ): Promise<FilletChamferResult> {
    const payload: FilletAllPayload = { brep, radius, tessellationOptions };
    return this.submitTask('fillet-all', payload);
  }

  /**
   * Apply fillet to selected edges
   */
  async filletSelectedEdges(
    brep: string,
    edgeIndices: number[],
    radius: number,
    tessellationOptions?: WorkerTessellationOptions
  ): Promise<FilletChamferResult> {
    const payload: FilletEdgesPayload = { brep, edgeIndices, radius, tessellationOptions };
    return this.submitTask('fillet-edges', payload);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // CHAMFER OPERATIONS
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Apply chamfer to all edges
   */
  async chamferAllEdges(
    brep: string,
    distance: number,
    tessellationOptions?: WorkerTessellationOptions
  ): Promise<FilletChamferResult> {
    const payload: ChamferAllPayload = { brep, distance, tessellationOptions };
    return this.submitTask('chamfer-all', payload);
  }

  /**
   * Apply chamfer to selected edges
   */
  async chamferSelectedEdges(
    brep: string,
    edgeIndices: number[],
    distance: number,
    tessellationOptions?: WorkerTessellationOptions
  ): Promise<FilletChamferResult> {
    const payload: ChamferEdgesPayload = { brep, edgeIndices, distance, tessellationOptions };
    return this.submitTask('chamfer-edges', payload);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // TESSELLATION
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Tessellate a shape to mesh arrays
   */
  async tessellate(
    brep: string,
    options?: WorkerTessellationOptions
  ): Promise<TessellationResult> {
    const payload: TessellatePayload = { brep, options };
    return this.submitTask('tessellate', payload);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // EXTRACTION
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Extract edge wireframe from a shape
   */
  async extractEdges(
    brep: string,
    samplesPerEdge: number = 20
  ): Promise<ExtractEdgesResult> {
    const payload: ExtractEdgesPayload = { brep, samplesPerEdge };
    return this.submitTask('extract-edges', payload);
  }

  /**
   * Extract individual face data from a shape
   */
  async extractFaces(
    brep: string,
    tessellationOptions?: WorkerTessellationOptions
  ): Promise<ExtractFacesResult> {
    const payload: ExtractFacesPayload = { brep, tessellationOptions };
    return this.submitTask('extract-faces', payload);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // TESSELLATION/EXTRACTION V2 (ShapeId-based, worker-only architecture)
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Tessellate a shape using shapeId (worker-only)
   */
  async tessellateV2(
    shapeId: string,
    options?: WorkerTessellationOptions
  ): Promise<TessellationResult> {
    const payload: TessellatePayloadV2 = { shapeId, options };
    return this.submitTask('tessellate-v2', payload);
  }

  /**
   * Extract edge wireframe using shapeId (worker-only)
   */
  async extractEdgesV2(
    shapeId: string,
    samplesPerEdge: number = 20
  ): Promise<ExtractEdgesResult> {
    const payload: ExtractEdgesPayloadV2 = { shapeId, samplesPerEdge };
    return this.submitTask('extract-edges-v2', payload);
  }

  /**
   * Extract individual face data using shapeId (worker-only)
   */
  async extractFacesV2(
    shapeId: string,
    tessellationOptions?: WorkerTessellationOptions
  ): Promise<ExtractFacesResult> {
    const payload: ExtractFacesPayloadV2 = { shapeId, tessellationOptions };
    return this.submitTask('extract-faces-v2', payload);
  }

  /**
   * Extract boundary wires for a specific face using shapeId (worker-only)
   */
  async extractFaceWiresV2(
    shapeId: string,
    faceIndex: number,
    samplesPerEdge: number = 32
  ): Promise<ExtractFaceWiresResult> {
    const payload: ExtractFaceWiresPayloadV2 = { shapeId, faceIndex, samplesPerEdge };
    return this.submitTask('extract-face-wires-v2', payload);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // SHELL & OFFSET
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Create a shell (hollow out a solid)
   */
  async shell(
    brep: string,
    thickness: number,
    faceIndicesToRemove?: number[],
    tessellationOptions?: WorkerTessellationOptions
  ): Promise<ShellOffsetResult> {
    const payload: ShellPayload = { brep, thickness, faceIndicesToRemove, tessellationOptions };
    return this.submitTask('shell', payload);
  }

  /**
   * Offset a shape
   */
  async offset(
    brep: string,
    distance: number,
    tessellationOptions?: WorkerTessellationOptions
  ): Promise<ShellOffsetResult> {
    const payload: OffsetPayload = { brep, distance, tessellationOptions };
    return this.submitTask('offset', payload);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PUSH/PULL
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Push or pull a face to modify a shape
   * @param brep Shape as BREP string
   * @param faceIndex Index of face to push/pull
   * @param distance Positive = push outward, negative = pull inward
   */
  async pushPull(
    brep: string,
    faceIndex: number,
    distance: number,
    tessellationOptions?: WorkerTessellationOptions
  ): Promise<PushPullResult> {
    const payload: PushPullPayload = { brep, faceIndex, distance, tessellationOptions };
    return this.submitTask('push-pull', payload);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // EXTRUDE
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Extrude a profile to create a solid
   * @param profilePoints Flat array of 3D points [x1,y1,z1, x2,y2,z2, ...]
   * @param direction Extrusion direction vector
   * @param height Extrusion height
   * @param holes Optional array of hole profiles
   */
  async extrude(
    profilePoints: number[],
    direction: [number, number, number],
    height: number,
    holes?: number[][],
    tessellationOptions?: WorkerTessellationOptions
  ): Promise<ExtrudeResult> {
    const payload: ExtrudePayload = { profilePoints, direction, height, holes, tessellationOptions };
    return this.submitTask('extrude', payload);
  }

  /**
   * Extrude a profile to create a solid - V2 (stores in worker registry)
   * @param resultShapeId ID to assign to the resulting shape
   * @param profilePoints Flat array of 3D points [x1,y1,z1, x2,y2,z2, ...]
   * @param direction Extrusion direction vector
   * @param height Extrusion height
   * @param holes Optional array of hole profiles (flat point arrays)
   */
  async extrudeV2(
    resultShapeId: string,
    profilePoints: number[],
    direction: [number, number, number],
    height: number,
    holes?: number[][],
    tessellationOptions?: WorkerTessellationOptions
  ): Promise<ExtrudeResultV2> {
    const payload: ExtrudePayloadV2 = { resultShapeId, profilePoints, direction, height, holes, tessellationOptions };
    return this.submitTask('extrude-v2', payload);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // IMPORT
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Import shapes from STEP content
   */
  async importStep(
    stepContent: string,
    filename?: string,
    tessellationOptions?: WorkerTessellationOptions
  ): Promise<StepImportResult> {
    const payload: ImportStepPayload = { stepContent, filename, tessellationOptions };
    return this.submitTask('import-step', payload);
  }

  /**
   * Import shapes from STEP content with streaming for large files.
   * Shapes are emitted via onStreamShape as they are parsed.
   * Subscribe to onStreamShape before calling this method.
   *
   * @param stepContent STEP file content as string
   * @param filename Optional filename for naming
   * @param options Import options (fastTessellation, skipHealing, skipEdges)
   * @returns Promise that resolves with StreamingImportComplete when done
   *
   * @example
   * ```typescript
   * // Subscribe to shapes as they arrive
   * this.workerService.onStreamShape.subscribe(shape => {
   *   console.log(`Shape ${shape.shapeIndex + 1}/${shape.totalShapes}: ${shape.name}`);
   *   // Add shape to scene immediately
   *   this.addShapeToScene(shape);
   * });
   *
   * // Start streaming import
   * const result = await this.workerService.importStepStreaming(content, 'model.step');
   * console.log(`Import complete: ${result.totalShapes} shapes in ${result.totalTime}ms`);
   * ```
   */
  async importStepStreaming(
    stepContent: string,
    filename?: string,
    options?: Omit<StepImportOptions, 'streaming'>
  ): Promise<StreamingImportComplete> {
    const payload = {
      stepContent,
      filename,
      options: {
        ...options,
        streaming: true,
        // Default to fast import for streaming (can be overridden)
        fastTessellation: options?.fastTessellation ?? true,
        skipHealing: options?.skipHealing ?? true,
        skipEdges: options?.skipEdges ?? true
      }
    };
    return this.submitTask('import-step', payload);
  }

  /**
   * Import shapes from STEP with optimized settings for large files.
   * Uses coarser tessellation, skips healing and edge extraction.
   * Does NOT stream - returns all shapes at once.
   *
   * @param stepContent STEP file content
   * @param filename Optional filename
   * @returns Promise with all imported shapes
   */
  async importStepFast(
    stepContent: string,
    filename?: string
  ): Promise<StepImportResult> {
    const payload = {
      stepContent,
      filename,
      options: {
        fastTessellation: true,
        skipHealing: true,
        skipEdges: true,
        streaming: false
      }
    };
    return this.submitTask('import-step', payload);
  }

  /**
   * Export a shape to STEP format
   * @param brep Shape as BREP string
   * @param filename Optional filename for the export
   */
  async exportStep(
    brep: string,
    filename?: string
  ): Promise<ExportStepResult> {
    const payload: ExportStepPayload = { brep, filename };
    return this.submitTask('export-step', payload);
  }

  /**
   * Import STEP file - V2 (shapes stored in registry, no BREP returned)
   */
  async importStepV2(
    stepContent: string,
    filename?: string,
    options?: StepImportOptions
  ): Promise<StepImportResultV2> {
    const payload: ImportStepPayloadV2 = { stepContent, filename, options };
    return this.submitTask('import-step-v2', payload);
  }

  /**
   * Export shape to STEP - V2 (uses shapeId from registry)
   */
  async exportStepV2(
    shapeId: string,
    filename?: string
  ): Promise<ExportStepResultV2> {
    const payload: ExportStepPayloadV2 = { shapeId, filename };
    return this.submitTask('export-step-v2', payload);
  }

  /**
   * Export multiple shapes to a single STEP file
   */
  async exportStepMultiV2(
    shapeIds: string[],
    filename?: string
  ): Promise<ExportStepMultiResultV2> {
    const payload: ExportStepMultiPayloadV2 = { shapeIds, filename };
    return this.submitTask('export-step-multi-v2', payload);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PRIMITIVE CREATION (Worker-only architecture)
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Create a box primitive in the worker registry
   */
  async createBox(
    shapeId: string,
    width: number,
    height: number,
    depth: number,
    position?: [number, number, number],
    tessellationOptions?: WorkerTessellationOptions
  ): Promise<CreateShapeResult> {
    const payload: CreateBoxPayload = { shapeId, width, height, depth, position, tessellationOptions };
    return this.submitTask('create-box', payload);
  }

  /**
   * Create a cylinder primitive in the worker registry
   */
  async createCylinder(
    shapeId: string,
    radius: number,
    height: number,
    position?: [number, number, number],
    tessellationOptions?: WorkerTessellationOptions
  ): Promise<CreateShapeResult> {
    const payload: CreateCylinderPayload = { shapeId, radius, height, position, tessellationOptions };
    return this.submitTask('create-cylinder', payload);
  }

  /**
   * Create a sphere primitive in the worker registry
   */
  async createSphere(
    shapeId: string,
    radius: number,
    position?: [number, number, number],
    tessellationOptions?: WorkerTessellationOptions
  ): Promise<CreateShapeResult> {
    const payload: CreateSpherePayload = { shapeId, radius, position, tessellationOptions };
    return this.submitTask('create-sphere', payload);
  }

  /**
   * Create a ring (hollow cylinder) primitive in the worker registry
   */
  async createRing(
    shapeId: string,
    outerRadius: number,
    innerRadius: number,
    height: number,
    position?: [number, number, number],
    tessellationOptions?: WorkerTessellationOptions
  ): Promise<CreateShapeResult> {
    const payload: CreateRingPayload = { shapeId, outerRadius, innerRadius, height, position, tessellationOptions };
    return this.submitTask('create-ring', payload);
  }

  /**
   * Create a cone primitive in the worker registry
   */
  async createCone(
    shapeId: string,
    radius1: number,
    radius2: number,
    height: number,
    position?: [number, number, number],
    tessellationOptions?: WorkerTessellationOptions
  ): Promise<CreateShapeResult> {
    const payload: CreateConePayload = { shapeId, radius1, radius2, height, position, tessellationOptions };
    return this.submitTask('create-cone', payload);
  }

  /**
   * Create a slot (stadium/discorectangle) primitive with proper arc end caps
   * Uses true arc geometry for clean CAD output
   */
  async createSlot(
    shapeId: string,
    startX: number,
    startY: number,
    endX: number,
    endY: number,
    width: number,
    height: number,
    position?: [number, number, number],
    tessellationOptions?: WorkerTessellationOptions
  ): Promise<CreateShapeResult> {
    const payload: CreateSlotPayload = { shapeId, startX, startY, endX, endY, width, height, position, tessellationOptions };
    return this.submitTask('create-slot', payload);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // SHAPE MANAGEMENT (Worker-only architecture)
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Clone a shape in the worker registry
   */
  async cloneShape(
    sourceShapeId: string,
    newShapeId: string,
    tessellationOptions?: WorkerTessellationOptions
  ): Promise<CreateShapeResult> {
    const payload: CloneShapePayload = { sourceShapeId, newShapeId, tessellationOptions };
    return this.submitTask('clone-shape', payload);
  }

  /**
   * Delete a shape from the worker registry
   */
  async deleteShape(shapeId: string): Promise<DeleteShapeResult> {
    const payload: DeleteShapePayload = { shapeId };
    return this.submitTask('delete-shape', payload);
  }

  /**
   * Delete multiple shapes from the worker registry
   */
  async deleteShapes(shapeIds: string[]): Promise<DeleteShapesResult> {
    const payload: DeleteShapesPayload = { shapeIds };
    return this.submitTask('delete-shapes', payload);
  }

  /**
   * Apply a 4x4 transformation matrix to a shape in the worker registry
   * @param shapeId The shape to transform
   * @param matrix 16-element array representing a 4x4 column-major transformation matrix
   */
  async transformShape(
    shapeId: string,
    matrix: number[],
    tessellationOptions?: WorkerTessellationOptions
  ): Promise<TransformShapeResult> {
    const payload: TransformShapePayload = { shapeId, matrix, tessellationOptions };
    return this.submitTask('transform-shape', payload);
  }

  /**
   * Get information about a shape (face count, edge count, etc.)
   */
  async getShapeInfo(shapeId: string): Promise<ShapeInfo> {
    const payload: GetShapeInfoPayload = { shapeId };
    return this.submitTask('get-shape-info', payload);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // V2 OPERATIONS (ShapeId-based, worker-only architecture)
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Boolean union using shapes from worker registry (no BREP serialization)
   */
  async booleanUnionV2(
    shapeIdA: string,
    shapeIdB: string,
    resultShapeId: string,
    deleteInputs: boolean = false,
    tessellationOptions?: WorkerTessellationOptions
  ): Promise<BooleanOperationResultV2> {
    const payload: BooleanPayloadV2 = {
      shapeIdA,
      shapeIdB,
      resultShapeId,
      deleteInputs,
      tessellationOptions
    };
    return this.submitTask('boolean-union-v2', payload);
  }

  /**
   * Boolean subtract using shapes from worker registry (no BREP serialization)
   */
  async booleanSubtractV2(
    shapeIdA: string,
    shapeIdB: string,
    resultShapeId: string,
    deleteInputs: boolean = false,
    tessellationOptions?: WorkerTessellationOptions
  ): Promise<BooleanOperationResultV2> {
    const payload: BooleanPayloadV2 = {
      shapeIdA,
      shapeIdB,
      resultShapeId,
      deleteInputs,
      tessellationOptions
    };
    return this.submitTask('boolean-subtract-v2', payload);
  }

  /**
   * Boolean intersect using shapes from worker registry (no BREP serialization)
   */
  async booleanIntersectV2(
    shapeIdA: string,
    shapeIdB: string,
    resultShapeId: string,
    deleteInputs: boolean = false,
    tessellationOptions?: WorkerTessellationOptions
  ): Promise<BooleanOperationResultV2> {
    const payload: BooleanPayloadV2 = {
      shapeIdA,
      shapeIdB,
      resultShapeId,
      deleteInputs,
      tessellationOptions
    };
    return this.submitTask('boolean-intersect-v2', payload);
  }

  /**
   * Fillet all edges using shape from worker registry
   */
  async filletAllV2(
    shapeId: string,
    resultShapeId: string,
    radius: number,
    deleteInput: boolean = false,
    tessellationOptions?: WorkerTessellationOptions
  ): Promise<FilletChamferResultV2> {
    const payload: FilletAllPayloadV2 = {
      shapeId,
      resultShapeId,
      radius,
      deleteInput,
      tessellationOptions
    };
    return this.submitTask('fillet-all-v2', payload);
  }

  /**
   * Fillet selected edges using shape from worker registry
   */
  async filletEdgesV2(
    shapeId: string,
    resultShapeId: string,
    edgeIndices: number[],
    radius: number,
    deleteInput: boolean = false,
    tessellationOptions?: WorkerTessellationOptions
  ): Promise<FilletChamferResultV2> {
    const payload: FilletEdgesPayloadV2 = {
      shapeId,
      resultShapeId,
      edgeIndices,
      radius,
      deleteInput,
      tessellationOptions
    };
    return this.submitTask('fillet-edges-v2', payload);
  }

  /**
   * Chamfer all edges using shape from worker registry
   */
  async chamferAllV2(
    shapeId: string,
    resultShapeId: string,
    distance: number,
    deleteInput: boolean = false,
    tessellationOptions?: WorkerTessellationOptions
  ): Promise<FilletChamferResultV2> {
    const payload: ChamferAllPayloadV2 = {
      shapeId,
      resultShapeId,
      distance,
      deleteInput,
      tessellationOptions
    };
    return this.submitTask('chamfer-all-v2', payload);
  }

  /**
   * Chamfer selected edges using shape from worker registry
   */
  async chamferEdgesV2(
    shapeId: string,
    resultShapeId: string,
    edgeIndices: number[],
    distance: number,
    deleteInput: boolean = false,
    tessellationOptions?: WorkerTessellationOptions
  ): Promise<FilletChamferResultV2> {
    const payload: ChamferEdgesPayloadV2 = {
      shapeId,
      resultShapeId,
      edgeIndices,
      distance,
      deleteInput,
      tessellationOptions
    };
    return this.submitTask('chamfer-edges-v2', payload);
  }

  /**
   * Shell operation using shape from worker registry
   */
  async shellV2(
    shapeId: string,
    resultShapeId: string,
    thickness: number,
    faceIndicesToRemove: number[] = [],
    deleteInput: boolean = false,
    tessellationOptions?: WorkerTessellationOptions
  ): Promise<ShellOffsetResultV2> {
    const payload: ShellPayloadV2 = {
      shapeId,
      resultShapeId,
      thickness,
      faceIndicesToRemove,
      deleteInput,
      tessellationOptions
    };
    return this.submitTask('shell-v2', payload);
  }

  /**
   * Offset operation using shape from worker registry
   */
  async offsetV2(
    shapeId: string,
    resultShapeId: string,
    distance: number,
    deleteInput: boolean = false,
    tessellationOptions?: WorkerTessellationOptions
  ): Promise<ShellOffsetResultV2> {
    const payload: OffsetPayloadV2 = {
      shapeId,
      resultShapeId,
      distance,
      deleteInput,
      tessellationOptions
    };
    return this.submitTask('offset-v2', payload);
  }

  /**
   * Push/pull face using shape from worker registry
   */
  async pushPullV2(
    shapeId: string,
    resultShapeId: string,
    faceIndex: number,
    distance: number,
    deleteInput: boolean = false,
    tessellationOptions?: WorkerTessellationOptions
  ): Promise<PushPullResultV2> {
    const payload: PushPullPayloadV2 = {
      shapeId,
      resultShapeId,
      faceIndex,
      distance,
      deleteInput,
      tessellationOptions
    };
    return this.submitTask('push-pull-v2', payload);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // UTILITIES
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Heal/fix a shape's topology
   */
  async healShape(
    brep: string,
    tessellationOptions?: WorkerTessellationOptions
  ): Promise<HealShapeResult> {
    const payload: HealShapePayload = { brep, tessellationOptions };
    return this.submitTask('heal-shape', payload);
  }

  /**
   * Heal shape using shapeId from registry (V2 - worker-only)
   */
  async healShapeV2(
    shapeId: string,
    resultShapeId: string,
    deleteInput: boolean = false,
    tessellationOptions?: WorkerTessellationOptions
  ): Promise<HealShapeResultV2> {
    const payload: HealShapePayloadV2 = { shapeId, resultShapeId, deleteInput, tessellationOptions };
    return this.submitTask('heal-shape-v2', payload);
  }

  /**
   * Extract a single face from a shape as a standalone surface (V2 - worker-only)
   */
  async extractFaceV2(
    shapeId: string,
    resultShapeId: string,
    faceIndex: number,
    deleteInput: boolean = false,
    tessellationOptions?: WorkerTessellationOptions
  ): Promise<ExtractFaceResultV2> {
    const payload: ExtractFacePayloadV2 = { shapeId, resultShapeId, faceIndex, deleteInput, tessellationOptions };
    return this.submitTask('extract-face-v2', payload);
  }

  /**
   * Extract multiple faces from a shape and sew them together (V2 - worker-only)
   */
  async extractMultipleFacesV2(
    shapeId: string,
    resultShapeId: string,
    faceIndices: number[],
    deleteInput: boolean = false,
    tessellationOptions?: WorkerTessellationOptions
  ): Promise<ExtractFaceResultV2> {
    const payload: ExtractMultipleFacesPayloadV2 = { shapeId, resultShapeId, faceIndices, deleteInput, tessellationOptions };
    return this.submitTask('extract-faces-multi-v2', payload);
  }

  /**
   * Thicken a surface/face to create a solid (V2 - worker-only)
   */
  async thickenSurfaceV2(
    shapeId: string,
    resultShapeId: string,
    thickness: number,
    deleteInput: boolean = false,
    tessellationOptions?: WorkerTessellationOptions
  ): Promise<ThickenSurfaceResultV2> {
    const payload: ThickenSurfacePayloadV2 = { shapeId, resultShapeId, thickness, deleteInput, tessellationOptions };
    return this.submitTask('thicken-surface-v2', payload);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // BREP SERIALIZATION (For project save/load)
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Export a shape from registry to BREP string (for project serialization)
   */
  async exportBrep(shapeId: string): Promise<ExportBrepResult> {
    const payload: ExportBrepPayload = { shapeId };
    return this.submitTask('export-brep', payload);
  }

  /**
   * Import a BREP string into registry (for project deserialization)
   */
  async importBrep(
    shapeId: string,
    brep: string,
    tessellationOptions?: WorkerTessellationOptions
  ): Promise<ImportBrepResult> {
    const payload: ImportBrepPayload = { shapeId, brep, tessellationOptions };
    return this.submitTask('import-brep', payload);
  }

  /**
   * Get the number of pending tasks
   */
  get pendingCount(): number {
    return this.pendingTasks.size;
  }

  /**
   * Terminate the worker
   */
  terminate(): void {
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }

    // Reject all pending tasks so callers don't hang
    const terminationError = new Error('Worker terminated');
    for (const [, task] of this.pendingTasks) {
      clearTimeout(task.timeoutId);
      task.reject(terminationError);
    }
    this.pendingTasks.clear();

    this._isReady = false;
    this.isReady$.next(false);
    this.isProcessing$.next(false);
    this.initPromise = null;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PRESERVE GEOMETRY (desktop port of cloud preserve_geometry Lambda)
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Import an assembly STEP file as a single compound shape (all roots
   * combined). Returns the shape ID to pass to the preserve algorithms.
   */
  async preserveImportAssembly(
    stepContent: string,
    shapeId?: string,
  ): Promise<PreserveImportAssemblyResult> {
    const payload: PreserveImportAssemblyPayload = { stepContent, shapeId };
    return this.submitTask('preserve-import-assembly', payload);
  }

  /**
   * Preview holes — identify cylindrical holes in the shape and return details
   * + grouped summary. Does not generate a STEP file.
   */
  async preservePreviewHoles(
    shapeId: string,
    maxDiameterMm = 50.0,
  ): Promise<PreservePreviewResult> {
    const payload: PreservePreviewPayload = { shapeId, maxDiameterMm };
    return this.submitTask('preserve-preview', payload);
  }

  /**
   * Generate preserves — identify holes, build hollow cylinders, export as STEP.
   * Returns STEP content as utf-8 string (caller writes to disk via IPC).
   */
  async preserveGenerate(
    shapeId: string,
    maxDiameterInches = 2.0,
    protrusionMm = 0,
    protrusionPercent = 0,
    maxDepthMm = 50.0,
    surfaceOffsetMm = 0,
  ): Promise<PreserveGenerateResult> {
    const payload: PreserveGeneratePayload = {
      shapeId,
      maxDiameterInches,
      protrusionMm,
      protrusionPercent,
      maxDepthMm,
      surfaceOffsetMm,
    };
    return this.submitTask('preserve-generate', payload);
  }

  /**
   * Generate preserves for the hole features the user clicked (manual
   * selection). Returns STEP content like preserveGenerate, plus how many
   * clicks matched nothing. With surfaceOffsetMm, opening / boss walls and flat
   * faces get skins and plates that thick.
   */
  async preserveGenerateFromClicks(
    shapeId: string,
    clicks: PreserveGenerateFromClicksPayload['clicks'],
    protrusionMm = 0,
    protrusionPercent = 0,
    surfaceOffsetMm = 0,
  ): Promise<PreserveGenerateFromClicksResult> {
    const payload: PreserveGenerateFromClicksPayload = { shapeId, clicks, protrusionMm, protrusionPercent, surfaceOffsetMm };
    return this.submitTask('preserve-generate-from-clicks', payload);
  }

  /**
   * Check body connectivity — analyse whether all solid bodies/shells in the
   * shape are connected via AABB overlap + centroid containment graph.
   */
  async preserveCheckConnectivity(
    shapeId: string,
    distanceToleranceMm = 0.01,
  ): Promise<PreserveConnectivityResult> {
    const payload: PreserveConnectivityPayload = {
      shapeId,
      distanceToleranceMm,
    };
    return this.submitTask('preserve-check-connectivity', payload);
  }

  ngOnDestroy(): void {
    this.terminate();
  }
}
